import React, { useState, useEffect, useRef, useContext } from 'react'
import PropTypes from 'prop-types'

import { DesktopCarousel } from '../../molecules/desktopCarousel'
import { ResponsiveContainer } from '../../atoms/responsiveContainer'
import { themeSizes } from '../../../styles/theme'
import { getActiveItems } from '../../../dataManipulation/cmsItems'
import { AuthContext } from '../../../context/authProvider'
import { CarouselFallback } from './fallback'
import { MobileCarousel } from '../../molecules/mobileCarousel'
import {
  normalizeDataLayerText,
  pushDataLayer,
} from '../../../utils/pushDataLayer'

const Carousel = (props) => {
  const {
    slides,
    dontShowLoading,
    setHideWrapperPadding,
    useGatsbyImage,
    screenName,
    triggerGAEvent,
  } = props
  const { isLoggedIn } = useContext(AuthContext)

  const filterSlides = () => {
    const visibilitySetting = isLoggedIn ? 'logged_in' : 'logged_out'

    const slidesByVisibilitySetting =
      slides?.filter(
        (slide) =>
          !slide.visibility_setting ||
          slide.visibility_setting === 'always' ||
          slide.visibility_setting === visibilitySetting
      ) ?? []

    return getActiveItems(slidesByVisibilitySetting)
  }

  const filteredSlides = filterSlides()

  useEffect(() => {
    if (setHideWrapperPadding && filteredSlides?.length === 0) {
      setHideWrapperPadding(true)
    }
  }, [filteredSlides, setHideWrapperPadding])

  const [index, setIndex] = useState(0)
  const [timeoutId, setTimeoutId] = useState(0)
  const { length } = filteredSlides
  const timeoutIdRef = useRef(timeoutId)
  timeoutIdRef.current = timeoutId

  const safeSetIndex = (newIndex, timeoutTime = 5000) => {
    setIndex(newIndex)
    clearTimeout(timeoutIdRef.current)
    const newTimeoutId = setTimeout(() => goToNext(newIndex), timeoutTime)
    setTimeoutId(newTimeoutId)
  }

  const onNext = () => {
    const newIndex = index + 1
    if (newIndex < length) safeSetIndex(newIndex)
    else safeSetIndex(0)
  }

  const onPrevious = () => {
    const newIndex = index - 1
    if (newIndex >= 0) safeSetIndex(newIndex)
    else safeSetIndex(length - 1)
  }

  const goToNext = (currentIndex) => {
    const newIndex = currentIndex + 1
    if (newIndex < length) safeSetIndex(newIndex)
    else safeSetIndex(0)
  }

  useEffect(() => {
    if (isLoggedIn === null || length <= 1) return
    safeSetIndex(0, 10000)
    return () => { 
      clearTimeout(timeoutId)
    }
  }, [isLoggedIn, length])

  const onBannerClick = ({ position, header_text, sub_text }) => {
    if (triggerGAEvent) {
      pushDataLayer({
        eventName: 'banner-opened',
        data: {
          location: screenName,
          banner_sub_text: normalizeDataLayerText(sub_text),
          banner_name: normalizeDataLayerText(header_text),
          position,
        },
      })
    }
  }

  if (!dontShowLoading && slides.length === 0) return <CarouselFallback />
  if (length === 0) return <></>

  return (
    <ResponsiveContainer
      sizeNumber={themeSizes.desktop}
      render={() => (
        <MobileCarousel
          key="MobileCarousel"
          slides={filteredSlides}
          index={index}
          onSlideBlur={() => safeSetIndex(index)}
          onSlideFocus={() => clearTimeout(timeoutId)}
          onNext={onNext}
          onPrevious={onPrevious}
          useGatsbyImage={useGatsbyImage}
          onBannerClick={onBannerClick}
        />
      )}
      renderElse={() => (
        <DesktopCarousel
          key="DesktopCarousel"
          slides={filteredSlides}
          index={index}
          onSlideBlur={() => safeSetIndex(index)}
          onSlideFocus={() => clearTimeout(timeoutId)}
          onNext={onNext}
          onPrevious={onPrevious}
          useGatsbyImage={useGatsbyImage}
          onBannerClick={onBannerClick}
        />
      )}
      renderFallback={() => (
        <CarouselFallback
          key="FallbackCarousel"
          index={index}
          length={length}
          slides={filteredSlides}
        />
      )}
    />
  )
}

Carousel.defaultProps = {
  dontShowLoading: false,
  slides: [],
  setHideWrapperPadding: null,
  useGatsbyImage: false,
}

Carousel.propTypes = {
  setHideWrapperPadding: PropTypes.func,
  dontShowLoading: PropTypes.bool,
  useGatsbyImage: PropTypes.bool,
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      background_color: PropTypes.string,
      header_tag: PropTypes.string,
      header_text: PropTypes.string,
      sub_text: PropTypes.string,
      text_color: PropTypes.string,
      visibility_setting: PropTypes.string,
      cta_logged_out: PropTypes.arrayOf(
        PropTypes.shape({
          action_text: PropTypes.string,
          link: PropTypes.string,
          background_color: PropTypes.string,
          text_color: PropTypes.string,
        })
      ),
      cta_logged_in: PropTypes.arrayOf(
        PropTypes.shape({
          action_text: PropTypes.string,
          link: PropTypes.string,
          background_color: PropTypes.string,
          text_color: PropTypes.string,
        })
      ),
      background_image: PropTypes.shape({
        alt: PropTypes.string,
        url: PropTypes.string,
        title: PropTypes.string,
      }),
    })
  ),
  screenName: PropTypes.string,
  triggerGAEvent: PropTypes.bool,
}

export { Carousel }
